/**
 * ErrorBoundary global do front-delivai.
 *
 * Captura erros não-tratados em árvores React (incluindo ApiClientError
 * lançado fora de try/catch). Envia para `console.error` + Sentry futuro,
 * mostra fallback amigável.
 */
import React from "react";
import { ApiClientError } from "../services/api/errors";

type Props = {
  children: React.ReactNode;
  fallback?: (err: Error, reset: () => void) => React.ReactNode;
};

type State = { error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const correlationId =
      error instanceof ApiClientError ? error.correlationId : undefined;
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", {
      error,
      info: info.componentStack,
      correlationId,
    });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): React.ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      const isApi = this.state.error instanceof ApiClientError;
      return (
        <div className="h-screen flex items-center justify-center bg-gradient-delivai px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Algo deu errado
            </h1>
            <p className="text-gray-600 mb-1">
              {isApi
                ? (this.state.error as ApiClientError).message
                : "Ocorreu um erro inesperado."}
            </p>
            {isApi && (this.state.error as ApiClientError).correlationId && (
              <p className="text-xs text-gray-400 mb-4">
                ID: {(this.state.error as ApiClientError).correlationId}
              </p>
            )}
            <div className="flex gap-3 justify-center mt-6">
              <button
                onClick={this.reset}
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300"
              >
                Tentar de novo
              </button>
              <button
                onClick={() => {
                  this.reset();
                  window.location.assign("/");
                }}
                className="px-4 py-2 rounded-lg bg-delivai-primary text-white font-medium hover:opacity-90"
              >
                Voltar ao início
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
